/**
 * 🃏 /admin/dfcl-decks — DFCL deck granting (and revoking) panel.
 *
 * Owner-only. Lets Roc hand out DFCL decks by typing an email and
 * clicking a tile — no curl, no API calls. Does three things:
 *   1. Look up a user by email (preview name + current owned decks)
 *   2. Grant any catalog deck to that user ($addToSet — idempotent)
 *   3. Revoke a granted deck ($pull — idempotent)
 *
 * Keeps the admin surface minimal — one row per catalog deck, Grant
 * on the right, Revoke if they already own it. No bulk actions.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, Search, UserCheck, Check, X, AlertCircle } from "lucide-react";
import { DFCL_STARTER_DECKS } from "@/data/dfclStarterDecks";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDFCLDecks() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const isOwner =
    user && (user.role === "owner" || (user.email || "").toLowerCase() === "omnimuscle@yahoo.com");

  const [email, setEmail]       = useState("");
  const [target, setTarget]     = useState(null);   // { user_id, email, name, dfcl_decks }
  const [lookupErr, setLookupErr] = useState(null);
  const [looking, setLooking]   = useState(false);
  const [busyDeck, setBusyDeck] = useState(null);   // deck_id currently being granted/revoked

  useEffect(() => { document.title = "🃏 DFCL Decks · Admin"; }, []);
  if (loading) return null;
  if (!isOwner) {
    return (
      <div className="max-w-3xl mx-auto p-8 text-center text-neutral-400" data-testid="admin-dfcl-locked">
        🔒 Owner only.
      </div>
    );
  }

  async function lookUp(e) {
    e?.preventDefault?.();
    setLookupErr(null);
    setTarget(null);
    const clean = (email || "").trim().toLowerCase();
    if (!clean) {
      setLookupErr("Enter an email.");
      return;
    }
    setLooking(true);
    try {
      const { data } = await axios.get(`${API}/admin/dfcl/decks/lookup`, {
        params: { email: clean },
      });
      setTarget(data);
    } catch (err) {
      setLookupErr(err?.response?.data?.detail || "Lookup failed.");
    } finally {
      setLooking(false);
    }
  }

  async function grant(deck_id) {
    if (!target) return;
    setBusyDeck(deck_id);
    try {
      const { data } = await axios.post(`${API}/admin/dfcl/decks/grant`, {
        deck_id,
        target_email: target.email,
      });
      if (data.already_owned) {
        toast.info(`${target.name || target.email} already owned "${deck_id}". No change.`);
      } else {
        toast.success(`Granted "${deck_id}" to ${target.name || target.email}.`);
      }
      setTarget({ ...target, dfcl_decks: data.dfcl_decks });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Grant failed.");
    } finally {
      setBusyDeck(null);
    }
  }

  async function revoke(deck_id) {
    if (!target) return;
    if (!window.confirm(`Revoke "${deck_id}" from ${target.name || target.email}?`)) return;
    setBusyDeck(deck_id);
    try {
      const { data } = await axios.post(`${API}/admin/dfcl/decks/revoke`, {
        deck_id,
        target_email: target.email,
      });
      toast.success(`Revoked "${deck_id}" from ${target.name || target.email}.`);
      setTarget({ ...target, dfcl_decks: data.dfcl_decks });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Revoke failed.");
    } finally {
      setBusyDeck(null);
    }
  }

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-8 text-neutral-200" data-testid="admin-dfcl-page">
      {/* Breadcrumb */}
      <button
        onClick={() => navigate("/admin")}
        className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest text-neutral-500 hover:text-amber-200 mb-5"
        data-testid="admin-dfcl-back"
      >
        <ArrowLeft size={14} /> Back to Admin
      </button>

      <div className="mb-6">
        <div className="text-[10px] uppercase tracking-[0.3em] text-amber-400 font-bold">🃏 DFCL</div>
        <h1 className="font-display text-3xl text-white mt-1">Deck Grants</h1>
        <p className="text-sm text-neutral-500 mt-2 max-w-xl leading-snug">
          Hand out Dragonious Flame Card Legends decks to specific accounts
          for playtesting. Grants are idempotent — safe to click twice.
        </p>
      </div>

      {/* ─── Lookup form ──────────────────────────────────────── */}
      <form
        onSubmit={lookUp}
        className="border border-zinc-800 bg-black/40 p-4 mb-5"
        data-testid="admin-dfcl-lookup-form"
      >
        <label className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 font-bold">
          Player Email
        </label>
        <div className="flex gap-2 mt-1.5">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="player@example.com"
            data-testid="admin-dfcl-email-input"
            className="flex-1 bg-black border border-zinc-700 px-3 py-2 text-sm text-white focus:border-amber-400 focus:outline-none"
            autoCapitalize="off"
            autoCorrect="off"
          />
          <button
            type="submit"
            disabled={looking || !email}
            data-testid="admin-dfcl-lookup-btn"
            className="inline-flex items-center gap-2 border border-amber-500/50 bg-amber-950/30 hover:border-amber-400 hover:bg-amber-950/50 text-amber-200 px-4 py-2 text-[10px] uppercase tracking-widest disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Search size={14} /> {looking ? "Looking…" : "Look Up"}
          </button>
        </div>
        {lookupErr && (
          <div className="mt-2 flex items-center gap-2 text-xs text-red-300" data-testid="admin-dfcl-lookup-err">
            <AlertCircle size={14} /> {lookupErr}
          </div>
        )}
      </form>

      {/* ─── Target player ────────────────────────────────────── */}
      {target && (
        <div
          className="border border-amber-500/40 bg-gradient-to-br from-[#1a1308] to-black p-4 mb-5"
          data-testid="admin-dfcl-target"
        >
          <div className="flex items-center gap-3 flex-wrap">
            <UserCheck size={18} className="text-amber-400 shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="font-display text-lg text-white leading-tight">
                {target.name}
              </div>
              <div className="text-[11px] text-neutral-400 font-mono truncate">
                {target.email} · {target.role || "user"} · <span className="text-neutral-600">{target.user_id?.slice(0, 10)}…</span>
              </div>
            </div>
            <div className="text-[10px] uppercase tracking-widest text-amber-300 border border-amber-500/40 bg-amber-950/30 px-2 py-1 shrink-0">
              {target.dfcl_decks.length} / {DFCL_STARTER_DECKS.length} owned
            </div>
          </div>
        </div>
      )}

      {/* ─── Deck catalog with per-deck action ─────────────────── */}
      {target && (
        <div className="space-y-2" data-testid="admin-dfcl-catalog">
          <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 font-bold mb-2">
            Catalog · {DFCL_STARTER_DECKS.length} decks
          </div>
          {DFCL_STARTER_DECKS.map((d) => {
            const owned = target.dfcl_decks.includes(d.id);
            const busy = busyDeck === d.id;
            return (
              <div
                key={d.id}
                data-testid={`admin-dfcl-row-${d.id}`}
                className="border border-zinc-800 bg-black/40 p-3 flex items-center gap-3 flex-wrap"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-display text-base text-white leading-tight">{d.name}</span>
                    {d.starter && (
                      <span className="text-[9px] uppercase tracking-widest text-emerald-300 border border-emerald-500/40 px-1.5 py-0.5">
                        STARTER
                      </span>
                    )}
                    {d.locked && (
                      <span className="text-[9px] uppercase tracking-widest text-amber-300 border border-amber-500/40 px-1.5 py-0.5">
                        LOCKED
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-neutral-500 font-mono">{d.id}</div>
                </div>
                {owned ? (
                  <>
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-emerald-300 border border-emerald-500/40 bg-emerald-950/20 px-2 py-1">
                      <Check size={11} /> Owned
                    </span>
                    <button
                      onClick={() => revoke(d.id)}
                      disabled={busy || d.starter}
                      title={d.starter ? "Starter decks can't be revoked" : "Revoke this deck"}
                      data-testid={`admin-dfcl-revoke-${d.id}`}
                      className="inline-flex items-center gap-1.5 border border-red-500/40 hover:border-red-400 text-red-300 hover:text-red-200 px-3 py-1.5 text-[10px] uppercase tracking-widest disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <X size={12} /> {busy ? "…" : "Revoke"}
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => grant(d.id)}
                    disabled={busy}
                    data-testid={`admin-dfcl-grant-${d.id}`}
                    className="inline-flex items-center gap-1.5 border border-amber-500/50 bg-amber-950/30 hover:border-amber-400 hover:bg-amber-950/50 text-amber-200 px-4 py-1.5 text-[10px] uppercase tracking-widest disabled:opacity-50"
                  >
                    {busy ? "Granting…" : "Grant →"}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {!target && !looking && !lookupErr && (
        <div className="text-center text-neutral-500 text-sm py-8 italic" data-testid="admin-dfcl-hint">
          Look up a player by email to see which decks they own and grant them more.
        </div>
      )}
    </div>
  );
}
